class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = null;
    this.maxPageWidth = parseFloat(
      getComputedStyle(this.closest('.shopify-section')).getPropertyValue('max-width')
    );
    this.resizeObserver = null;
  }

  connectedCallback() {
    this.slider = this.initMainSlider();

    if (this.querySelector('[data-ref="thumbnail-slider"]')) {
      this.thumbnailSlider = this.initThumbnailSlider();
    }

    if (this.thumbnailSlider) {
      this.slider.sync(this.thumbnailSlider);
    }

    this.slider.mount();

    if (this.thumbnailSlider) {
      this.thumbnailSlider.mount();
    }

    /* Injecte les flèches custom à la place des dots de pagination */
    this.maybeAddCustomArrows();

    if (!this.thumbnailSlider) {
      this.slider.on('mounted', () => {
        this.updateSize();
      });

      this.resizeObserver = new ResizeObserver(() => {
        this.updateSize();
      });
      this.resizeObserver.observe(document.documentElement);
    }
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  /**
   * Remplace les dots de pagination par deux flèches gauche/droite en bas.
   * S'active uniquement si :
   *  - Le slider a un élément .splide
   *  - La pagination existe dans le DOM
   *  - Le slider est en overflow (plus de slides que de slots visibles)
   *  - Le slider n'est PAS un thumbnail/nav slider (isNavigation)
   */
  maybeAddCustomArrows() {
    const splideEl = this.querySelector('.splide');
    if (!splideEl) return;

    const pagination = splideEl.querySelector('.splide__pagination');
    if (!pagination) return;

    /* Ne pas toucher aux sliders qui ne sont pas en overflow (pas assez de slides) */
    if (!splideEl.classList.contains('is-overflow')) return;

    /* Ne pas toucher aux thumbnail sliders (navigation) */
    if (this.querySelector('[data-ref="thumbnail-slider"]')) return;

    /* Marque le splide pour cacher les dots via CSS */
    splideEl.classList.add('has-custom-arrows');

    /* Crée le conteneur des flèches */
    const arrowsContainer = document.createElement('div');
    arrowsContainer.className = 'splide__custom-arrows';

    /* Bouton précédent */
    const prevBtn = document.createElement('button');
    prevBtn.className = 'splide__custom-arrow splide__custom-arrow--prev';
    prevBtn.setAttribute('type', 'button');
    prevBtn.setAttribute('aria-label', 'Slide précédent');
    prevBtn.innerHTML =
      '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><polyline points="15 18 9 12 15 6"></polyline></svg>';

    /* Bouton suivant */
    const nextBtn = document.createElement('button');
    nextBtn.className = 'splide__custom-arrow splide__custom-arrow--next';
    nextBtn.setAttribute('type', 'button');
    nextBtn.setAttribute('aria-label', 'Slide suivant');
    nextBtn.innerHTML =
      '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><polyline points="9 6 15 12 9 18"></polyline></svg>';

    arrowsContainer.appendChild(prevBtn);
    arrowsContainer.appendChild(nextBtn);

    /* Insère les flèches juste après la pagination dans le DOM */
    pagination.parentNode.insertBefore(arrowsContainer, pagination.nextSibling);

    /* Ajoute une classe si le slider contient un cross-sell */
    if (this.querySelector('cross-sell-component')) {
      splideEl.classList.add('has-custom-arrows--cross-sell');
    }

    /* Navigation au clic */
    prevBtn.addEventListener('click', () => {
      this.slider.go('<');
    });

    nextBtn.addEventListener('click', () => {
      this.slider.go('>');
    });

    /* Met à jour l'état disabled selon la position du slider */
    const updateArrowState = () => {
      const index = this.slider.index;
      const end = this.slider.Components.Controller.getEnd();
      const isRewind = this.slider.options.rewind;

      if (isRewind) {
        /* En mode rewind, les deux flèches sont toujours actives */
        prevBtn.disabled = false;
        nextBtn.disabled = false;
      } else {
        prevBtn.disabled = index === 0;
        nextBtn.disabled = index >= end;
      }
    };

    this.slider.on('moved', updateArrowState);
    this.slider.on('refresh', updateArrowState);

    /* État initial */
    updateArrowState();
  }

  initMainSlider() {
    const perPageDesktop = this.getAttribute('data-per-page-desktop');
    let perPageMobile = this.getAttribute('data-per-page-mobile');
    const gap = this.getAttribute('data-gap') + 'px';
    const showArrows = this.getAttribute('data-show-arrows') === 'true';
    const showArrowsMobile = this.getAttribute('data-show-arrows-mobile') === 'true';
    const showPagination = this.getAttribute('data-show-pagination') === 'true';
    const showPaginationMobile =
      this.getAttribute('data-show-pagination-mobile') === 'true';
    const autoplay = this.getAttribute('data-autoplay') === 'true';
    const autoplaySpeed = this.getAttribute('data-autoplay-speed') + '000';
    const rewind = this.getAttribute('data-rewind') === 'true';

    if (perPageMobile === '1.8') perPageMobile = 1;

    return new Splide(this.querySelector('[data-ref="main-slider"]'), {
      perPage: perPageMobile,
      perMove: 1,
      rewind: rewind,
      gap: gap,
      drag: true,
      snap: true,
      arrows: showArrows,
      arrowsMobile: showArrowsMobile,
      pagination: showPaginationMobile,
      autoplay: autoplay,
      interval: autoplaySpeed,
      autoHeight: true,
      lazyLoad: false,
      flickMaxPages: 1,
      flickPower: 400,
      mediaQuery: 'min',
      breakpoints: {
        750: {
          perPage: perPageDesktop,
          pagination: showPagination,
          arrows: showArrows,
        },
      },
    });
  }

  initThumbnailSlider() {
    const showArrows =
      this.getAttribute('data-show-arrows-thumbnails') === 'true';
    const isVertical =
      this.getAttribute('data-thumbnails-vertical') === 'true';
    const isDesktop = window.innerWidth > 750;

    const thumbnailSlider = new Splide(this.querySelector('[data-ref="thumbnail-slider"]'), {
      fixedWidth: 80,
      fixedHeight: 80,
      gap: 10,
      rewind: false,
      pagination: false,
      isNavigation: true,
      arrows: showArrows,
      ...(isVertical &&
        isDesktop && {
          direction: 'ttb',
          height: '400px',
        }),
      ...(!isVertical || !isDesktop
        ? {
            breakpoints: {
              750: {
                fixedWidth: 70,
                fixedHeight: 70,
              },
            },
          }
        : {}),
    });

    // Synchronise la hauteur des thumbnails avec l'image principale après chargement
    if (isVertical && isDesktop) {
      const mainSlider = this.querySelector('[data-ref="main-slider"]');
      if (mainSlider) {
        const firstImage = mainSlider.querySelector('.splide__slide img');
        if (firstImage) {
          const syncHeight = () => {
            const slideEl = mainSlider.querySelector('.splide__slide');
            if (slideEl && slideEl.offsetHeight > 0) {
              thumbnailSlider.options = { height: slideEl.offsetHeight + 'px' };
              thumbnailSlider.refresh();
            }
          };

          if (firstImage.complete) {
            // Image déjà en cache, on attend juste le prochain frame
            requestAnimationFrame(syncHeight);
          } else {
            firstImage.addEventListener('load', syncHeight, { once: true });
          }
        }
      }
    }

    return thumbnailSlider;
  }

  updateSize() {
    this.pageWidth = document.documentElement.clientWidth;
    this.sectionPadding = parseFloat(
      getComputedStyle(this.closest('.shopify-section')).getPropertyValue(
        'padding-right'
      )
    );

    var padding = this.sectionPadding;
    if (this.pageWidth > this.maxPageWidth) {
      padding =
        (this.pageWidth - this.maxPageWidth) / 2 + this.sectionPadding;
    }

    this.style.width = `calc(100% + ${2 * padding}px)`;
    this.style.marginLeft = `-${padding}px`;
    this.style.marginRight = `-${padding}px`;

    if (this.querySelector('.splide__track')) {
      this.querySelector('.splide__track').style.paddingRight = `${padding}px`;
      this.querySelector('.splide__track').style.paddingLeft = `${padding}px`;
    }

    if (
      this.querySelector('.splide__arrow--prev') &&
      this.querySelector('.splide__arrow--next')
    ) {
      this.querySelector('.splide__arrow--prev').style.left = `${padding + 10}px`;
      this.querySelector('.splide__arrow--next').style.right = `${padding + 10}px`;
    }
  }

  remove(index) {
    this.slider.remove(index);
  }
}

if (!customElements.get('slider-component')) {
  customElements.define('slider-component', SliderComponent);
}